import { Link, Navigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Reveal from "@/components/Reveal";
import { PHOTOS, getCity, getCountry } from "@/data/destinations";
import { getCityContent } from "@/data/cityContent";
import { useLang } from "@/i18n/LanguageContext";

export default function CityPage() {
  const { countrySlug, citySlug } = useParams();
  const { lang, t } = useLang();
  const city = getCity(countrySlug, citySlug);
  const country = getCountry(countrySlug);

  if (!city || !country) return <Navigate to="/" replace />;

  const content = getCityContent(city.slug, lang);
  const gallery = [city.img, ...(country.gallery || []).filter((g) => g !== city.img)].slice(0, 3);
  const highlights = content?.highlights || [t("city.h1"), t("city.h2"), t("city.h3")];
  const experiences = [t("city.e1"), t("city.e2"), t("city.e3")];

  return (
    <main data-testid="city-page">
      <Navbar />

      <section className="relative flex min-h-[64vh] items-end overflow-hidden">
        <img src={city.img} alt={city.name} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050814] via-[#050814]/45 to-[#050814]/30" />
        <div className="relative mx-auto w-full max-w-[1500px] px-6 pb-16 pt-40 lg:px-12">
          <Link
            to={`/destinations/${country.slug}`}
            data-testid="city-back-link"
            className="mb-8 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-white/60 transition-colors hover:text-[#d4af37]"
          >
            <ArrowLeft size={14} /> {country.name}
          </Link>
          <h1 className="font-serif text-6xl font-light tracking-tight text-white sm:text-7xl lg:text-8xl" data-testid="city-title">
            <span className="mask-line">
              <motion.span
                initial={{ y: "115%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
              >
                {city.name}
              </motion.span>
            </span>
          </h1>
          {content?.tagline && (
            <p className="mt-4 font-serif text-2xl font-light italic text-white/75 lg:text-3xl" data-testid="city-tagline">
              {content.tagline}
            </p>
          )}
          <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.3em] text-[#e8cf7a]/80" data-testid="city-meta">
            {city.airport ? `${city.airport} · ` : ""}{city.lat.toFixed(2)}° / {city.lon.toFixed(2)}°
          </p>
        </div>
      </section>

      <section className="py-20 lg:py-24">
        <div className="mx-auto grid max-w-[1500px] gap-12 px-6 lg:grid-cols-3 lg:px-12">
          <Reveal>
            <p className="eyebrow">{content ? t("city.whyTitle") : t("country.overview")}</p>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-2">
            <p className="max-w-2xl font-serif text-2xl font-light leading-relaxed text-white/85 lg:text-3xl" data-testid="city-intro">
              {content?.why || t("city.intro", { city: city.name, country: country.name })}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-white/10 py-20 lg:py-24" data-testid="city-highlights">
        <div className="mx-auto max-w-[1500px] px-6 lg:px-12">
          <Reveal>
            <h2 className="mb-12 font-serif text-3xl font-light text-white lg:text-4xl">{t("city.highlights")}</h2>
          </Reveal>
          <div className="grid gap-10 md:grid-cols-3">
            {highlights.map((h, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div className="border-t border-[#d4af37]/40 pt-6" data-testid={`city-highlight-${i}`}>
                  <span className="font-mono text-sm text-[#d4af37]">0{i + 1}</span>
                  <p className="mt-3 font-serif text-xl font-light leading-snug text-white/85">{h}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-white/10 py-20 lg:py-24" data-testid="city-experiences">
        <div className="mx-auto max-w-[1500px] px-6 lg:px-12">
          <Reveal>
            <h2 className="mb-12 font-serif text-3xl font-light text-white lg:text-4xl">{t("city.experiences")}</h2>
          </Reveal>
          <div className="grid gap-6 md:grid-cols-3">
            {experiences.map((e, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <article className="group relative h-80 overflow-hidden border border-white/10" data-testid={`city-experience-${i}`}>
                  <img src={gallery[i % gallery.length]} alt="" loading="lazy" className="img-spotlight absolute inset-0 h-full w-full object-cover opacity-60" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#050814] via-[#050814]/40 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-6">
                    <span className="font-mono text-[10px] tracking-[0.3em] text-[#d4af37]">0{i + 1}</span>
                    <p className="mt-2 font-serif text-xl font-light leading-snug text-white">{e}</p>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-white/10 py-20 lg:py-24" data-testid="city-services">
        <div className="mx-auto max-w-[1500px] px-6 lg:px-12">
          <Reveal>
            <h2 className="mb-10 font-serif text-3xl font-light text-white lg:text-4xl">{t("city.services")}</h2>
          </Reveal>
          <div className="flex flex-wrap gap-3">
            {["hotels", "transfers", "guides", "chauffeur", "tours", "vip"].map((id) => (
              <span key={id} data-testid={`city-service-${id}`} className="border border-white/15 px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] text-white/70">
                {t(`svc.${id}.t`)}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-24" data-testid="city-alpha-guide">
        <div className="mx-auto max-w-[1500px] px-6 lg:px-12">
          <Reveal>
            <div className="grid overflow-hidden border border-[#d4af37]/30 bg-[#0a1128] lg:grid-cols-5">
              <div className="relative h-64 lg:col-span-2 lg:h-auto">
                <img src={PHOTOS.concierge} alt="Alpha Guide" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#0a1128]/60" />
              </div>
              <div className="p-10 lg:col-span-3 lg:p-16">
                <p className="eyebrow mb-4">{t("guide.eyebrow")}</p>
                <h3 className="font-serif text-3xl font-light text-white lg:text-4xl">
                  {t("city.guideTitle", { city: city.name })}
                </h3>
                <p className="mt-5 max-w-lg text-base font-light leading-relaxed text-white/60">
                  {t("city.guideText", { city: city.name })}
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="pb-20 lg:pb-24" data-testid="city-gallery">
        <div className="mx-auto max-w-[1500px] px-6 lg:px-12">
          <Reveal>
            <h2 className="mb-10 font-serif text-3xl font-light text-white lg:text-4xl">{t("city.gallery")}</h2>
          </Reveal>
          <div className="grid gap-5 md:grid-cols-3">
            {gallery.map((g, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div className="clipped-frame group overflow-hidden" data-testid={`city-gallery-${i}`}>
                  <img src={g} alt={`${city.name} ${i + 1}`} loading="lazy" className="img-spotlight aspect-[4/3] w-full object-cover" />
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <Link
              to="/#concierge"
              data-testid="city-cta"
              className="btn-gold group mt-14 inline-flex items-center gap-3 px-8 py-4 text-sm font-semibold tracking-wide"
            >
              {t("city.cta", { city: city.name })}
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      <Footer />
    </main>
  );
}
